import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Upload, X, Check, Loader2, CheckCircle2, CalendarClock, Info,
} from "lucide-react";
import { PublicHeader } from "../components/Layout";
import { useI18n } from "../lib/i18n";
import { api, euro, fileUrl, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const MATERIALS = ["PVC", "Aluminium", "Hout"];
const QUANTITIES = [1, 2, 3, 4, "5+"];
const TOTAL = 8;

function Option({ selected, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all font-medium ${
        selected ? "border-brand-orange bg-orange-50 text-slate-900" : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
      }`}
    >
      <span className="flex items-center justify-between">
        {children}
        {selected && <Check size={18} className="text-brand-orange" />}
      </span>
    </button>
  );
}

export default function BookingWizard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);

  const [form, setForm] = useState({
    service_id: "", material: "", item_type: "raam", quantity: 1, photos: [],
    location: { street: "", house_number: "", postal_code: "", city: "" },
    contact: { first_name: "", last_name: "", phone: "", email: "" },
    appointment_preference: "to_be_scheduled",
  });

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = params.get("service");
      if (pre && r.data.some((s) => s.id === pre)) setForm((f) => ({ ...f, service_id: pre }));
    });
  }, [params]);

  const selectedService = services.find((s) => s.id === form.service_id);
  const qtyNum = form.quantity === "5+" ? 5 : Number(form.quantity);
  const price = selectedService ? selectedService.base_price * qtyNum : 0;
  const estLow = Math.max(0, Math.floor((price * 0.6) / 10) * 10);
  const estHigh = Math.ceil((price * 1.2) / 10) * 10;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLoc = (patch) => setForm((f) => ({ ...f, location: { ...f.location, ...patch } }));
  const setContact = (patch) => setForm((f) => ({ ...f, contact: { ...f.contact, ...patch } }));

  const canNext = useCallback(() => {
    if (step === 1) return !!form.service_id;
    if (step === 2) return !!form.material && !!form.item_type;
    if (step === 3) return !!form.quantity;
    if (step === 4) return true;
    if (step === 5) return form.location.street && form.location.house_number && form.location.postal_code && form.location.city;
    if (step === 6) return form.contact.first_name && form.contact.last_name && form.contact.phone && /\S+@\S+\.\S+/.test(form.contact.email);
    if (step === 7) return !!form.appointment_preference;
    return true;
  }, [step, form]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, photos: [...f.photos, { id: data.id, path: data.path, filename: data.filename }] }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id) => setForm((f) => ({ ...f, photos: f.photos.filter((p) => p.id !== id) }));

  const submitBooking = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: form.service_id,
        problem_type: selectedService ? selectedService.name_nl : "",
        material: form.material,
        item_type: form.item_type,
        quantity: qtyNum,
        photos: form.photos,
        location: form.location,
        contact: form.contact,
        appointment_preference: form.appointment_preference,
      };
      const { data } = await api.post("/bookings", payload);
      setBooking(data);
      setStep(10);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const progress = Math.min(step, TOTAL);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <PublicHeader />
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-8 pb-28 sm:pb-8">
        {step <= TOTAL && (
          <>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-semibold text-slate-900">{t("wizard.step")} {progress} {t("wizard.of")} {TOTAL}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden mb-8">
              <div className="h-full bg-brand-orange transition-all duration-300" style={{ width: `${(progress / TOTAL) * 100}%` }} />
            </div>
          </>
        )}

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            {/* STEP 1 */}
            {step === 1 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">{t("wizard.s1.title")}</h1>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.map((s) => (
                    <Option key={s.id} testId={`wizard-service-${s.id}`} selected={form.service_id === s.id} onClick={() => set({ service_id: s.id })}>
                      <span>{lang === "fr" ? s.name_fr : s.name_nl}</span>
                    </Option>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("wizard.s2.title")}</h1>
                <Label className="mb-2 block font-semibold">{t("wizard.s2.material")}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {MATERIALS.map((m) => (
                    <Option key={m} testId={`wizard-material-${m}`} selected={form.material === m} onClick={() => set({ material: m })}>{m}</Option>
                  ))}
                  <Option testId="wizard-material-unknown" selected={form.material === "Ik weet het niet"} onClick={() => set({ material: "Ik weet het niet" })}>{t("wizard.dontknow")}</Option>
                </div>
                <Label className="mb-2 mt-6 block font-semibold">{t("wizard.s2.type")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Option testId="wizard-type-raam" selected={form.item_type === "raam"} onClick={() => set({ item_type: "raam" })}>{t("wizard.raam")}</Option>
                  <Option testId="wizard-type-deur" selected={form.item_type === "deur"} onClick={() => set({ item_type: "deur" })}>{t("wizard.deur")}</Option>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("wizard.s3.title")}</h1>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {QUANTITIES.map((q) => (
                    <Option key={q} testId={`wizard-qty-${q}`} selected={form.quantity === q} onClick={() => set({ quantity: q })}>
                      <span className="text-center w-full text-lg font-bold">{q}</span>
                    </Option>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">{t("wizard.s4.title")}</h1>
                <p className="text-slate-600 mb-6">{t("wizard.s4.help")}</p>
                <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white p-10 cursor-pointer hover:border-brand-orange transition-colors" data-testid="wizard-upload-label">
                  {uploading ? <Loader2 className="animate-spin text-brand-orange" /> : <Upload className="text-slate-400" />}
                  <span className="font-semibold text-slate-700">{t("wizard.s4.upload")}</span>
                  <span className="text-xs text-slate-400">{t("wizard.s4.optional")}</span>
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleUpload} data-testid="wizard-file-input" />
                </label>
                {form.photos.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {form.photos.map((p) => (
                      <div key={p.id} className="relative group">
                        <img src={fileUrl(p.path)} alt={p.filename} className="h-24 w-full object-cover rounded-lg border border-slate-200" />
                        <button onClick={() => removePhoto(p.id)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`remove-photo-${p.id}`}><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 5 */}
            {step === 5 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("wizard.s5.title")}</h1>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2"><Label>{t("wizard.street")}</Label><Input value={form.location.street} onChange={(e) => setLoc({ street: e.target.value })} data-testid="wizard-street" className="mt-1" /></div>
                  <div><Label>{t("wizard.house")}</Label><Input value={form.location.house_number} onChange={(e) => setLoc({ house_number: e.target.value })} data-testid="wizard-house" className="mt-1" /></div>
                  <div><Label>{t("wizard.postal")}</Label><Input value={form.location.postal_code} onChange={(e) => setLoc({ postal_code: e.target.value })} data-testid="wizard-postal" className="mt-1" /></div>
                  <div className="sm:col-span-2"><Label>{t("wizard.city")}</Label><Input value={form.location.city} onChange={(e) => setLoc({ city: e.target.value })} data-testid="wizard-city" className="mt-1" /></div>
                </div>
              </div>
            )}

            {/* STEP 6 */}
            {step === 6 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("wizard.s6.title")}</h1>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>{t("wizard.firstname")}</Label><Input value={form.contact.first_name} onChange={(e) => setContact({ first_name: e.target.value })} data-testid="wizard-firstname" className="mt-1" /></div>
                  <div><Label>{t("wizard.lastname")}</Label><Input value={form.contact.last_name} onChange={(e) => setContact({ last_name: e.target.value })} data-testid="wizard-lastname" className="mt-1" /></div>
                  <div><Label>{t("wizard.phone")}</Label><Input value={form.contact.phone} onChange={(e) => setContact({ phone: e.target.value })} data-testid="wizard-phone" className="mt-1" /></div>
                  <div><Label>{t("wizard.email")}</Label><Input type="email" value={form.contact.email} onChange={(e) => setContact({ email: e.target.value })} data-testid="wizard-email" className="mt-1" /></div>
                </div>
              </div>
            )}

            {/* STEP 7 */}
            {step === 7 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-3">{t("wizard.s7.title")}</h1>
                <div className="rounded-xl border-2 border-slate-200 bg-white p-6 flex items-start gap-3" data-testid="wizard-appointment-info">
                  <CalendarClock className="text-brand-orange shrink-0" />
                  <p className="text-slate-600">{t("wizard.s7.info")}</p>
                </div>
                {selectedService && (
                  <div className="mt-4 rounded-xl border-2 border-orange-200 bg-orange-50 p-6" data-testid="wizard-price-estimate">
                    <p className="text-sm font-semibold text-slate-600">{t("wizard.estimate.label")}</p>
                    <p className="mt-1 font-heading text-3xl font-extrabold text-slate-900" data-testid="wizard-estimate-range">
                      {euro(estLow)} <span className="text-slate-400 font-bold">–</span> {euro(estHigh)}
                    </p>
                    <p className="mt-3 text-xs text-slate-500 flex items-start gap-1.5">
                      <Info size={14} className="shrink-0 mt-0.5" /> {t("wizard.estimate.note")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* STEP 8 - overview */}
            {step === 8 && (
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("wizard.s8.title")}</h1>
                <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="wizard-summary">
                  <Row label={t("wizard.summary.repair")} value={selectedService ? (lang === "fr" ? selectedService.name_fr : selectedService.name_nl) : ""} />
                  <Row label={t("wizard.summary.material")} value={`${form.material} · ${form.item_type === "raam" ? t("wizard.raam") : t("wizard.deur")}`} />
                  <Row label={t("wizard.summary.qty")} value={String(form.quantity)} />
                  <Row label={t("wizard.summary.location")} value={`${form.location.street} ${form.location.house_number}, ${form.location.postal_code} ${form.location.city}`} />
                  <Row label={t("wizard.summary.appointment")} value={t("wizard.to_be_scheduled")} />
                  <div className="flex items-center justify-between p-4 bg-slate-50">
                    <span className="font-heading font-bold text-slate-900">{t("wizard.estimate.label")}</span>
                    <span className="font-heading text-xl font-extrabold text-brand-orange" data-testid="wizard-estimate-total">{euro(estLow)} – {euro(estHigh)}</span>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-b-xl text-xs text-slate-500">
                    {t("wizard.price_note")}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 10 - done */}
            {step === 10 && (
              <div className="text-center py-10">
                <CheckCircle2 className="mx-auto text-green-500" size={64} />
                <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-6">{t("wizard.done.title")}</h1>
                <p className="text-slate-600 mt-3 max-w-md mx-auto">{t("wizard.done.msg")}</p>
                <div className="mt-6 inline-block bg-white rounded-xl border border-slate-200 px-8 py-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">{t("wizard.done.number")}</p>
                  <p className="font-heading text-2xl font-extrabold text-brand-orange" data-testid="wizard-booking-number">{booking?.booking_number}</p>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/account"><Button className="cta-orange rounded-full px-6" data-testid="wizard-goto-account">{t("wizard.done.account")}</Button></Link>
                  <Link to="/"><Button variant="outline" className="rounded-full px-6">{t("wizard.done.home")}</Button></Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* NAV BUTTONS (desktop) */}
        {step <= 8 && (
          <div className="hidden sm:flex items-center justify-between mt-10">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="wizard-back-btn">
              <ArrowLeft size={18} className="mr-1" /> {t("wizard.back")}
            </Button>
            {step < 8 ? (
              <Button className="cta-orange rounded-full px-7" onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="wizard-next-btn">
                {t("wizard.next")} <ArrowRight size={18} className="ml-1" />
              </Button>
            ) : (
              <Button className="cta-orange rounded-full px-7" onClick={submitBooking} disabled={submitting} data-testid="wizard-confirm-btn">
                {submitting ? <Loader2 className="animate-spin" /> : t("wizard.confirm")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* STICKY MOBILE BAR */}
      {step <= 8 && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 flex items-center gap-3 z-40">
          {step > 1 && (
            <Button variant="outline" className="rounded-full" onClick={() => setStep((s) => Math.max(1, s - 1))} data-testid="wizard-back-btn-mobile">
              <ArrowLeft size={18} />
            </Button>
          )}
          {step < 8 ? (
            <Button className="cta-orange rounded-full flex-1" onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="wizard-next-btn-mobile">
              {t("wizard.next")} <ArrowRight size={18} className="ml-1" />
            </Button>
          ) : (
            <Button className="cta-orange rounded-full flex-1" onClick={submitBooking} disabled={submitting} data-testid="wizard-confirm-btn-mobile">
              {submitting ? <Loader2 className="animate-spin" /> : t("wizard.confirm")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between p-4">
      <span className="text-slate-500 text-sm">{label}</span>
      <span className="font-medium text-slate-900 text-sm text-right">{value}</span>
    </div>
  );
}
